import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Users, KeyRound, ArrowRight, Check, Clock, ClipboardCheck, AlertCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const EmployeeLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledCode = searchParams.get("code") || "";

  const [code, setCode] = useState(prefilledCode);
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!code || !pin) {
      setError("Bitte Code und PIN eingeben");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/employee-auth/login`, {
        code: code.toUpperCase(),
        pin
      });

      if (res.data.success) {
        // Store employee session in sessionStorage (more secure than localStorage)
        sessionStorage.setItem("employee_session", res.data.session_token);
        sessionStorage.setItem("employee_data", JSON.stringify(res.data.employee));
        sessionStorage.setItem("business_name", res.data.business_name);
        
        toast.success(`Willkommen, ${res.data.employee.name}!`);
        navigate("/mitarbeiter-portal");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Anmeldung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4" data-testid="employee-login-page">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/30">
            <Users className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Mitarbeiter-Login</h1>
          <p className="text-slate-400 text-sm">MargeVio Aufgaben & Checklisten</p>
        </div>

        {/* Login Card */}
        <div className="bg-slate-800/90 backdrop-blur-sm border border-slate-700 rounded-2xl p-6 shadow-xl">
          <form onSubmit={handleLogin} className="space-y-4">
            {/* Code Input */}
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide text-slate-400 font-medium">
                Zugangscode
              </label>
              <Input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Z.B. DEMO02"
                maxLength={6}
                className="bg-slate-700/50 border-slate-600 text-white placeholder-slate-500 text-center text-lg font-mono tracking-widest"
                data-testid="employee-code-input"
              />
            </div>

            {/* PIN Input */}
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-wide text-slate-400 font-medium flex items-center gap-2">
                <KeyRound size={12} /> PIN
              </label>
              <Input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="4-stellige PIN"
                maxLength={4}
                className="bg-slate-700/50 border-slate-600 text-white placeholder-slate-500 text-center text-lg font-mono tracking-widest"
                data-testid="employee-pin-input"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full py-6 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold text-base"
              data-testid="employee-login-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Anmelden...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Anmelden <ArrowRight size={18} />
                </span>
              )}
            </Button>
          </form>

          {/* Info */}
          <div className="mt-6 pt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 text-center">
              Du hast Code und PIN von deinem Arbeitgeber erhalten.
            </p>
          </div>
        </div>

        {/* Features Preview */}
        <div className="mt-8 grid grid-cols-3 gap-3 text-center">
          <div className="bg-slate-800/50 rounded-lg p-3">
            <ClipboardCheck className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
            <span className="text-xs text-slate-400">Checklisten</span>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <Check className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <span className="text-xs text-slate-400">Aufgaben</span>
          </div>
          <div className="bg-slate-800/50 rounded-lg p-3">
            <Clock className="w-5 h-5 text-amber-400 mx-auto mb-1" />
            <span className="text-xs text-slate-400">Schichten</span>
          </div>
        </div>

        {/* Demo Hint */}
        {prefilledCode && (
          <div className="mt-6 bg-violet-500/10 border border-violet-500/30 rounded-lg p-4 text-center">
            <p className="text-sm text-violet-300">
              <strong>Demo-Login:</strong> Code ist vorausgefüllt.
              <br />
              <span className="text-violet-400">PIN: 2345</span>
            </p>
          </div>
        )}

        {/* Back Link */}
        <div className="mt-6 text-center">
          <a href="/" className="text-sm text-slate-500 hover:text-slate-300">
            ← Zurück zur Startseite
          </a>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLogin;
